import {
  BlockOutlined,
  BulbOutlined,
  CodeSandboxOutlined,
  DashboardOutlined,
  FireOutlined,
  FontColorsOutlined,
  GlobalOutlined,
  HistoryOutlined,
  LineOutlined,
  MailOutlined,
  PushpinOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  TagOutlined,
  TeamOutlined,
  UngroupOutlined,
  UnorderedListOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Channel,
  checkPermission,
  GET_CHANNELS,
  GET_ME,
  Me,
  PERMISSIONS,
} from '@frontend/data-access';
import { Layout, Menu } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';

const Sider: React.FC = () => {
  const { pathname } = useRouter();
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const { publicRuntimeConfig } = getConfig();
  const { data } = useQuery<{ me: Me }>(GET_ME);
  const { data: channelData } = useQuery<{
    channels: Channel[];
  }>(GET_CHANNELS);

  const isIdeaAssigned =
    channelData?.channels.filter((item) => item.ideasCount).length > 0;

  const menuItems: any = [
    {
      key: 'dashboard',
      link: '/',
      label: t('dashboard'),
      icon: <DashboardOutlined />,
      roles: ['ADMIN'],
    },
    {
      key: 'idea-hub',
      link: '/idea-hub',
      label: t('idea_hub'),
      icon: <BlockOutlined />,
      roles: ['ADMIN'],
      permissions: isIdeaAssigned
        ? [
            PERMISSIONS.ADMIN,
            PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
            PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT,
          ]
        : [PERMISSIONS.ADMIN, PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT],
    },
    {
      key: 'content-group',
      label: t('contents'),
      roles: ['ADMIN'],
      type: 'group',
      children: [
        {
          key: 'content-management',
          link: '/content-management',
          label: t('posts_and_events'),
          icon: <ReadOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'sidebar-management',
          link: '/sidebar-management',
          label: t('sidebar'),
          icon: <PushpinOutlined />,
          roles: ['ADMIN'],
        },
      ],
    },

    {
      key: 'challenge-group',
      label: t('challenges'),
      roles: ['ADMIN'],
      type: 'group',
      permissions: [
        PERMISSIONS.COMMUNITY_MANAGER,
        PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT,
        PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
      ],
      children: [
        {
          key: 'open-innovation',
          label: t('open_innovation'),
          icon: <GlobalOutlined />,
          roles: ['ADMIN'],
          permissions: [
            PERMISSIONS.COMMUNITY_MANAGER,
            PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT,
          ],
          children: [
            {
              key: 'open-challenges',
              icon: <LineOutlined />,
              link: '/entrepreneurship/challenge',
              label: t('open_challenges'),
              roles: ['ADMIN'],
              permissions: [PERMISSIONS.OPEN_CHALLENGE_MANAGEMENT],
            },
            {
              key: 'community',
              icon: <LineOutlined />,
              link: '/community',
              label: t('community'),
              roles: ['ADMIN'],
              permissions: [PERMISSIONS.COMMUNITY_MANAGER],
            },
          ],
        },
        {
          key: 'intra-challenges',
          link: '/intrapreneurship/challenge',
          label: t('intra_challenges'),
          icon: <FireOutlined />,
          roles: ['ADMIN'],
          permissions: [PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT],
        },
        {
          key: 'space-management',
          link: '/space-management',
          label: t('spaces'),
          icon: <UngroupOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'idea-management',
          link: '/idea-management',
          label: t('ideas'),
          icon: <BulbOutlined />,
          roles: ['ADMIN'],
        },
      ],
    },
    {
      key: 'manage-group',
      label: t('manage'),
      roles: ['ADMIN'],
      type: 'group',
      permissions: [PERMISSIONS.A3_MANAGER, PERMISSIONS.COMMUNITY_MANAGER],
      children: [
        {
          key: 'user-management',
          link: '/user-management',
          label: t('users'),
          icon: <UserOutlined />,
          roles: ['ADMIN'],
          permissions: [PERMISSIONS.A3_MANAGER],
        },
        {
          key: 'company-management',
          link: '/company-management',
          label: t('companies'),
          icon: <TeamOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'tags',
          link: '/tag-management',
          label: tcommon('tags'),
          icon: <TagOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'topics',
          link: '/topic-management',
          label: tcommon('topics'),
          icon: <FontColorsOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'pitch-management',
          link: '/pitch-management',
          label: t('pitch_videos'),
          icon: <VideoCameraOutlined />,
          roles: ['ADMIN'],
        },
        // {
        //   key: 'announcement',
        //   link: '/announcement',
        //   label: t('announcement'),
        //   icon: <MailOutlined />,
        //   roles: ['ADMIN'],
        //   permissions: [PERMISSIONS.COMMUNITY_MANAGER],
        // },
        {
          key: 'proof-of-concept',
          link: '/proof-of-concept',
          label: t('proof_of_concept'),
          icon: <CodeSandboxOutlined />,
          roles: ['ADMIN'],
        },
        {
          key: 'survey',
          link: '/survey',
          label: t('surveys'),
          icon: <QuestionCircleOutlined />,
          roles: ['ADMIN'],
          permissions: [PERMISSIONS.COMMUNITY_MANAGER],
        },
        {
          key: 'help-desk',
          link: '/guide',
          label: t('help_desk'),
          icon: <HistoryOutlined />,
          roles: ['ADMIN'],
        },
      ],
    },
  ];

  const addLinkLabel = (item: any) => {
    if (item.link) {
      return <Link href={item.link}>{item.label}</Link>;
    }
    return item.label;
  };

  const filteredItems = data?.me
    ? menuItems
        .map((item) => {
          if (item.type === 'group') {
            const children = item.children
              ?.filter(
                (child: any) =>
                  child.roles.includes(data.me.role) ||
                  checkPermission(data.me, child.permissions)
              )
              .map((child: any) => ({
                ...child,
                label: addLinkLabel(child),
                children: child.children
                  ? child.children
                      .filter(
                        (subChild: any) =>
                          subChild.roles.includes(data.me.role) ||
                          checkPermission(data.me, subChild.permissions)
                      )
                      .map((subChild: any) => ({
                        ...subChild,
                        label: addLinkLabel(subChild),
                      }))
                  : undefined,
              }));
            return children && children.length ? { ...item, children } : null;
          }
          return {
            ...item,
            label: addLinkLabel(item),
          };
        })
        .filter(
          (item) =>
            item &&
            (item.roles.includes(data.me.role) ||
              checkPermission(data.me, item.permissions))
        )
    : [];

  // Find the first available key, looking inside children if necessary
  const findActiveKey = (items: any[]): string | undefined => {
    for (const item of items) {
      if (item.link === pathname) return item.key;
      if (item.children && item.children.length) {
        const childKey = findActiveKey(item.children);
        if (childKey) return childKey;
      }
    }
    return undefined;
  };

  const [page, setPage] = useState<string>(findActiveKey(filteredItems));

  useEffect(() => {
    setPage(findActiveKey(filteredItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const siderStyle: React.CSSProperties = {
    height: '100vh',
    position: 'sticky',
    insetInlineStart: 0,
    top: 0,
    bottom: 0,
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable',
  };

  return (
    <Layout.Sider
      width={230}
      collapsedWidth={60}
      breakpoint="lg"
      style={siderStyle}
    >
      <div className="px-4 pt-3 pb-2">
        <Link href="/">
          <Image
            src={`${publicRuntimeConfig.staticFolder}/logo-full.svg`}
            priority
            height="40"
            width="166"
            alt={tcommon('scv_logo')}
          />
        </Link>
      </div>
      <div className="h-[calc(100vh-64px)] admin-sidebar-menu flex flex-col justify-between overflow-auto">
        <Menu
          mode="inline"
          selectedKeys={[page]}
          defaultOpenKeys={
            pathname?.startsWith('/entrepreneurship')
              ? ['entrepreneurship']
              : pathname?.startsWith('/intrapreneurship')
              ? ['intrapreneurship']
              : []
          }
          style={{ borderRight: 0 }}
          items={filteredItems}
        />
        {checkPermission(data.me, [PERMISSIONS.ADMIN]) && (
          <div className="mb-6">
            <Menu
              mode="inline"
              selectedKeys={pathname.includes('audit-log') ? ['audit-log'] : []}
              style={{ borderRight: 0 }}
              items={[
                {
                  key: 'audit-log',
                  label: <Link href="/audit-log">{t('audit_log')}</Link>,
                  icon: <UnorderedListOutlined />,
                },
              ]}
            />
          </div>
        )}
      </div>
    </Layout.Sider>
  );
};

export default Sider;
